import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SCENES } from './planetSections';

const vertexShader = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  uniform sampler2D tColor;
  uniform sampler2D tDepth;
  uniform vec2 uTexel;
  uniform float uNear;
  uniform float uFar;
  uniform float uFocusDistance;
  uniform float uFocusRange;
  uniform float uMaxBlur;
  varying vec2 vUv;

  float viewDistance(float depth) {
    float viewZ = (uNear * uFar) / ((uFar - uNear) * depth - uFar);
    return -viewZ;
  }

  void main() {
    vec4 sharp = texture2D(tColor, vUv);
    float depth = texture2D(tDepth, vUv).x;

    if (depth > 0.9999 || sharp.a < 0.002 || uMaxBlur < 0.05) {
      gl_FragColor = sharp;
      return;
    }

    float distanceFromFocus = abs(viewDistance(depth) - uFocusDistance);
    float coc = smoothstep(uFocusRange * 0.45, uFocusRange * 2.25, distanceFromFocus);
    float radius = coc * uMaxBlur;
    vec2 px = uTexel * radius;

    vec4 soft = sharp * 2.0;
    soft += texture2D(tColor, vUv + vec2( 1.0,  0.0) * px);
    soft += texture2D(tColor, vUv + vec2(-1.0,  0.0) * px);
    soft += texture2D(tColor, vUv + vec2( 0.5,  0.866) * px);
    soft += texture2D(tColor, vUv + vec2(-0.5,  0.866) * px);
    soft += texture2D(tColor, vUv + vec2( 0.5, -0.866) * px);
    soft += texture2D(tColor, vUv + vec2(-0.5, -0.866) * px);
    soft /= 8.0;

    gl_FragColor = mix(sharp, soft, coc * 0.92);
  }
`;

function smoothstep(from: number, to: number, value: number) {
  const t = THREE.MathUtils.clamp((value - from) / (to - from), 0, 1);
  return t * t * (3 - 2 * t);
}

export interface CinematicDepthOfFieldProps {
  enabled?: boolean;
}

export function CinematicDepthOfField({
  enabled = true,
}: CinematicDepthOfFieldProps) {
  const focusDistance = useRef(SCENES[0].cameraPos[2]);
  const focusPoint = useMemo(() => new THREE.Vector3(), []);
  const target = useMemo(() => {
    const depthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedShortType);
    depthTexture.format = THREE.DepthFormat;
    return new THREE.WebGLRenderTarget(1, 1, {
      depthBuffer: true,
      stencilBuffer: false,
      depthTexture,
    });
  }, []);
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          tColor: { value: target.texture },
          tDepth: { value: target.depthTexture },
          uTexel: { value: new THREE.Vector2(1, 1) },
          uNear: { value: 0.1 },
          uFar: { value: 100 },
          uFocusDistance: { value: focusDistance.current },
          uFocusRange: { value: SCENES[0].cameraFocusRange },
          uMaxBlur: { value: 0 },
        },
        vertexShader,
        fragmentShader,
        depthWrite: false,
        depthTest: false,
        transparent: true,
        toneMapped: false,
      }),
    [target],
  );
  const postCamera = useMemo(() => new THREE.Camera(), []);
  const postScene = useMemo(() => {
    const scene = new THREE.Scene();
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    quad.frustumCulled = false;
    scene.add(quad);
    return scene;
  }, [material]);

  useEffect(
    () => () => {
      const quad = postScene.children[0] as THREE.Mesh | undefined;
      quad?.geometry.dispose();
      material.dispose();
      target.dispose();
    },
    [material, postScene, target],
  );

  useFrame((state, delta) => {
    const { gl, scene, camera, size } = state;
    const pixelRatio = gl.getPixelRatio();
    const width = Math.max(1, Math.floor(size.width * pixelRatio));
    const height = Math.max(1, Math.floor(size.height * pixelRatio));
    if (target.width !== width || target.height !== height) {
      target.setSize(width, height);
      material.uniforms.uTexel.value.set(1 / width, 1 / height);
    }

    const sceneIndex =
      typeof window !== 'undefined' ? (window.__planetSceneIndex ?? 0) : 0;
    const chapter = SCENES[sceneIndex] ?? SCENES[0];
    const nextChapter = SCENES[Math.min(sceneIndex + 1, SCENES.length - 1)] ?? chapter;
    // Begin pulling focus shortly before the camera leaves a chapter. This
    // makes the next landmark resolve through the flight instead of snapping
    // sharp only after the camera has already arrived.
    const chapterBlend =
      typeof window !== 'undefined' ? (window.__planetBlendIn ?? 0) : 0;
    const focusPull =
      sceneIndex < SCENES.length - 1 ? smoothstep(0.52, 0.96, chapterBlend) : 0;
    focusPoint.set(
      THREE.MathUtils.lerp(chapter.cameraLookAt[0], nextChapter.cameraLookAt[0], focusPull),
      THREE.MathUtils.lerp(chapter.cameraLookAt[1], nextChapter.cameraLookAt[1], focusPull),
      THREE.MathUtils.lerp(chapter.cameraLookAt[2], nextChapter.cameraLookAt[2], focusPull),
    );
    const desiredFocus = camera.position.distanceTo(focusPoint);
    const focusEase = 1 - Math.pow(0.88, Math.min(delta, 1 / 30) * 60);
    focusDistance.current += (desiredFocus - focusDistance.current) * focusEase;

    const perspective = camera as THREE.PerspectiveCamera;
    const speed =
      typeof window !== 'undefined'
        ? Math.abs(window.__planetScrollVelocity ?? 0)
        : 0;
    const mobileScale = size.width < 861 ? 0.72 : 1;
    material.uniforms.uNear.value = perspective.near;
    material.uniforms.uFar.value = perspective.far;
    material.uniforms.uFocusDistance.value = focusDistance.current;
    material.uniforms.uFocusRange.value = THREE.MathUtils.lerp(
      chapter.cameraFocusRange,
      nextChapter.cameraFocusRange,
      focusPull,
    );
    material.uniforms.uMaxBlur.value = enabled
      ? chapter.cameraMaxBlur * mobileScale * (1 + speed * 0.45)
      : 0;

    const previousTarget = gl.getRenderTarget();
    gl.setRenderTarget(target);
    gl.clear();
    gl.render(scene, camera);
    gl.setRenderTarget(previousTarget);
    gl.clear();
    gl.render(postScene, postCamera);
  }, 1);

  return null;
}

export default CinematicDepthOfField;
